import { Type } from "@sinclair/typebox";
/* Дома слов: de Duolingo-stand van Rick en Nastya. XP bepaalt de hoogte van het
   huis, de serie het licht. Bewust een eigen endpoint en NIET in de bootstrap
   (zie DayWeatherResponse): oude bundels kennen het niet en merken niets.

   Licht:
     done   — vandaag een les gedaan: alle ramen aan
     wait   — de serie loopt nog vanaf gisteren, vandaag nog niets
     broken — de serie is voorbij (of er was er geen)
   `stale`: de laatste meting is niet van vandaag (Duolingo onbereikbaar). Het
   licht volgt dan de stand van die laatste dag, zodat een dicht endpoint een
   huis nooit donker maakt; alleen een gebroken serie doet dat. */
export const DuolingoState = Type.Union([Type.Literal("done"), Type.Literal("wait"), Type.Literal("broken")]);
export const DuolingoPerson = Type.Object({
    streak: Type.Integer({ minimum: 0 }),
    totalXp: Type.Integer({ minimum: 0 }),
    streakStart: Type.Union([Type.String(), Type.Null()]),
    streakEnd: Type.Union([Type.String(), Type.Null()]),
    state: DuolingoState,
    date: Type.String(), // lokale dag van de meting
    fetchedAt: Type.String(),
    stale: Type.Boolean(),
}, { additionalProperties: false });
export const DuolingoResponse = Type.Object({
    people: Type.Record(Type.String(), DuolingoPerson),
}, { additionalProperties: false });
/** De dag vóór `date` (YYYY-MM-DD), kalenderrekenen in UTC. */
export function dayBefore(date) {
    const d = new Date(`${date}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() - 1);
    return d.toISOString().slice(0, 10);
}
/** Het licht van een huis, gezien vanaf `today` (lokale dag van die persoon). */
export function duolingoState(streak, streakEnd, today) {
    if (streak <= 0 || !streakEnd)
        return "broken";
    if (streakEnd >= today)
        return "done";
    return streakEnd === dayBefore(today) ? "wait" : "broken";
}
/* Leer-XP uit de app, naast Duolingo in het huis van de woorden. Elke bron geeft per dag XP en het aantal goede en foute
   antwoorden. skola (N leert Nederlands) en ruskola (R leert Russisch) stuurt de telefoon als absolute dagtotalen
   (`PUT /api/v1/learning/:date/:source`); nn (N, Naar Nederland) ook, sinds de les-XP (12.24.0). Wat de oude app aan NN
   deed rekent de server nog zelf uit haar voortgang, en goed/fout van NN blijven afgeleid (met Ricks oordelen). Bij duolingo is
   `xp` de Duolingo-XP van die dag (0 als die onbekend is) en zijn right/wrong altijd 0. `lessons`: afgemaakte lessen van ≥ 5
   vragen die dag, voor het lessenkaartje (×2 vanaf les 4, ×3 vanaf les 6); ontbreekt bij duolingo. */
export const LearningStat = Type.Object({
    xp: Type.Integer({ minimum: 0 }),
    right: Type.Integer({ minimum: 0 }),
    wrong: Type.Integer({ minimum: 0 }),
    lessons: Type.Optional(Type.Integer({ minimum: 0 })),
}, { additionalProperties: false });
export const LearningSources = Type.Object({
    duolingo: Type.Optional(LearningStat),
    skola: Type.Optional(LearningStat),
    ruskola: Type.Optional(LearningStat),
    nn: Type.Optional(LearningStat),
}, { additionalProperties: false });
/** De scholen die de telefoon zelf stuurt, met hun enige leerling. */
export const LEARNING_SCHOOLS = { skola: "N", ruskola: "R", nn: "N" };
/** De bewaarde dag van één school: wat `PUT /api/v1/learning/:date/:source` teruggeeft. */
export const LearningDay = Type.Object({
    date: Type.String(),
    handle: Type.Union([Type.Literal("R"), Type.Literal("N")]),
    source: Type.Union([Type.Literal("skola"), Type.Literal("ruskola"), Type.Literal("nn")]),
    xp: Type.Integer({ minimum: 0 }),
    right: Type.Integer({ minimum: 0 }),
    wrong: Type.Integer({ minimum: 0 }),
    lessons: Type.Integer({ minimum: 0 }),
    updatedAt: Type.String(),
}, { additionalProperties: false });
/* De strijd: de XP per dag. `xp` is het verschil met de vorige bewaarde dag van die persoon (Duolingo geeft alleen het totaal);
   `null` voor de eerste bewaarde dag, want daar is geen vorige. `gap`: er ontbreekt minstens één dag tussen die twee metingen,
   het verschil telt dan op de latere dag. Tot en met vandaag; vandaag is een tussenstand.
   `sources`: de XP per bron die dag. Een dag met alleen app-XP staat er ook, met `xp: null`, `gap: false` en de serie en
   het totaal van de laatste Duolingo-dag ervoor (0 zonder). */
export const DuolingoHistoryDay = Type.Object({
    date: Type.String(),
    xp: Type.Union([Type.Integer({ minimum: 0 }), Type.Null()]),
    gap: Type.Boolean(),
    streak: Type.Integer({ minimum: 0 }),
    totalXp: Type.Integer({ minimum: 0 }),
    sources: Type.Optional(LearningSources),
}, { additionalProperties: false });
/* `appTotal`: alle app-XP van die persoon (vanaf LEARNING_XP_SINCE als die gezet is); `total`: het laatste Duolingo-totaal plus `appTotal`. */
export const DuolingoHistoryPerson = Type.Object({
    days: Type.Array(DuolingoHistoryDay),
    appTotal: Type.Optional(Type.Integer({ minimum: 0 })),
    total: Type.Optional(Type.Integer({ minimum: 0 })),
}, { additionalProperties: false });
/* `hasAny`: er is iets om te laten zien, een Duolingo-meting of app-XP, van wie dan ook. */
export const DuolingoHistoryResponse = Type.Object({
    people: Type.Record(Type.String(), DuolingoHistoryPerson),
    hasAny: Type.Optional(Type.Boolean()),
}, { additionalProperties: false });
/** De dagcijfers uit de bewaarde stand: oplopend op datum, per dag het verschil met de vorige bewaarde dag. */
export function duolingoHistory(rows) {
    const out = [];
    let prev = null;
    for (const r of [...rows].sort((a, b) => a.date.localeCompare(b.date))) {
        out.push({
            date: r.date,
            xp: prev ? Math.max(0, r.total_xp - prev.total_xp) : null,
            gap: prev ? prev.date !== dayBefore(r.date) : false,
            streak: r.streak,
            totalXp: r.total_xp,
        });
        prev = r;
    }
    return out;
}
//# sourceMappingURL=duolingo.js.map